<template>
  <div id="event-manual-entry" class="premium-dashboard">
    <v-overlay :value="ProgressLoadingFlag" z-index="999">
      <div class="overlay-loader-content text-center pa-6">
        <v-progress-circular
          indeterminate
          size="64"
          width="5"
          color="#0097D7"
        ></v-progress-circular>
        <p class="mt-4 font-weight-bold text-subtitle-1 white--text mb-0">
          Processing, please wait...
        </p>
      </div>
    </v-overlay>

    <!-- Massive Hero Dashboard Header -->
    <v-card class="hero-card mb-6" flat>
      <div class="hero-glow-effect"></div>

      <!-- Floating Decorative Ambient Icons -->
      <v-icon class="deco-icon icon-cash" color="rgba(255, 255, 255, 0.08)"
        >mdi-cash-register</v-icon
      >
      <v-icon class="deco-icon icon-ticket" color="rgba(255, 255, 255, 0.08)"
        >mdi-ticket</v-icon
      >
      <v-icon class="deco-icon icon-shield" color="rgba(255, 255, 255, 0.08)"
        >mdi-shield-check</v-icon
      >
      <v-icon class="deco-icon icon-rupee" color="rgba(255, 255, 255, 0.08)"
        >mdi-currency-inr</v-icon
      >

      <!-- Ocean Wave Background Animation -->
      <div class="waves-container">
        <svg
          class="waves"
          xmlns="http://www.w3.org/2000/svg"
          xmlns:xlink="http://www.w3.org/1999/xlink"
          viewBox="0 24 150 28"
          preserveAspectRatio="none"
          shape-rendering="auto"
        >
          <defs>
            <path
              id="gentle-wave-eme"
              d="M-160 44c30 0 58-18 88-18s58 18 88 18 58-18 88-18 58 18 88 18v44h-352z"
            />
          </defs>
          <g class="parallax">
            <use
              xlink:href="#gentle-wave-eme"
              x="48"
              y="0"
              fill="rgba(255, 255, 255, 0.05)"
            />
            <use
              xlink:href="#gentle-wave-eme"
              x="48"
              y="3"
              fill="rgba(255, 255, 255, 0.08)"
            />
            <use
              xlink:href="#gentle-wave-eme"
              x="48"
              y="5"
              fill="rgba(255, 255, 255, 0.03)"
            />
            <use
              xlink:href="#gentle-wave-eme"
              x="48"
              y="7"
              fill="rgba(255, 255, 255, 0.07)"
            />
          </g>
        </svg>
      </div>

      <div class="relative-content pa-6 pa-md-8">
        <v-row align="center" justify="space-between">
          <v-col cols="12" md="8">
            <div class="d-flex align-center title-container">
              <div class="header-icon-box mr-4 pulse-animation">
                <v-icon large color="white">mdi-cash-register</v-icon>
              </div>
              <div>
                <h1 class="hero-title animate-title white--text mb-1">
                  {{ PageTitle || "Event Manual Entry" }}
                </h1>
                <p class="hero-subtitle animate-subtitle white--text mb-0">
                  {{
                    PageDescription ||
                    "Record offline event payments one at a time or in bulk"
                  }}
                </p>
              </div>
            </div>
          </v-col>
          <v-col cols="12" md="4" class="text-md-right mt-4 mt-md-0">
            <div class="d-flex justify-md-end flex-wrap gap-3">
              <v-btn
                @click.prevent="refreshPageData"
                color="rgba(255,255,255,0.15)"
                elevation="0"
                rounded
                large
                class="header-action-btn mr-3 mb-2 mb-sm-0"
                title="Refresh page data"
              >
                <v-icon left color="white">mdi-refresh</v-icon>
                Refresh
              </v-btn>
              <v-btn
                @click.prevent="redirectPreviousPage"
                color="rgba(255,255,255,0.15)"
                elevation="0"
                rounded
                large
                class="header-action-btn"
                title="Back"
              >
                <v-icon left color="white">mdi-arrow-left</v-icon>
                Back
              </v-btn>
            </div>
          </v-col>
        </v-row>
      </div>
    </v-card>

    <!-- Animated Summary Statistics Row -->
    <v-row class="mb-6" v-if="EventId != '' || PlanId != ''">
      <v-col cols="12" sm="6" lg="3" class="py-2">
        <v-card class="stat-card glass-card stat-card-1">
          <div class="d-flex justify-content-between align-items-center">
            <div>
              <span class="stat-label">Selected Event</span>
              <h3 class="stat-value text-truncate" style="max-width: 170px">
                {{ selectedEventText }}
              </h3>
              <div class="stat-sublabel primary--text font-weight-bold mt-1">
                <v-chip
                  x-small
                  color="#3A67B1"
                  class="white--text font-weight-bold"
                >
                  {{ EventId ? "Active Event" : "Not Selected" }}
                </v-chip>
              </div>
            </div>
            <v-avatar color="rgba(58, 103, 177, 0.1)" size="48">
              <v-icon color="#3A67B1">mdi-calendar</v-icon>
            </v-avatar>
          </div>
        </v-card>
      </v-col>

      <v-col cols="12" sm="6" lg="3" class="py-2">
        <v-card class="stat-card glass-card stat-card-2">
          <div class="d-flex justify-content-between align-items-center">
            <div>
              <span class="stat-label">Selected Plan</span>
              <h3 class="stat-value text-truncate" style="max-width: 170px">
                {{ selectedPlanText }}
              </h3>
              <div class="stat-sublabel font-weight-bold mt-1">
                <span v-if="Amount" class="success--text font-weight-bold">
                  ₹{{ parseFloat(Amount || 0).toLocaleString("en-IN") }}
                </span>
                <span v-else class="text-muted">Standard Rate</span>
              </div>
            </div>
            <v-avatar color="rgba(0, 151, 215, 0.1)" size="48">
              <v-icon color="#0097D7">mdi-format-list-bulleted</v-icon>
            </v-avatar>
          </div>
        </v-card>
      </v-col>

      <v-col cols="12" sm="6" lg="3" class="py-2">
        <v-card class="stat-card glass-card stat-card-3">
          <div class="d-flex justify-content-between align-items-center">
            <div>
              <span class="stat-label">Entry Mode</span>
              <h3 class="stat-value text-capitalize">
                {{ EntryMode == "single" ? "Single Entry" : "Bulk Upload" }}
              </h3>
              <div class="stat-sublabel font-weight-bold mt-1 text-muted">
                {{
                  EntryMode == "single" ? "Manual Form" : "Excel File Import"
                }}
              </div>
            </div>
            <v-avatar color="rgba(237, 190, 56, 0.15)" size="48">
              <v-icon color="#EDBE38">
                {{
                  EntryMode == "single" ? "mdi-account-check" : "mdi-file-excel"
                }}
              </v-icon>
            </v-avatar>
          </div>
        </v-card>
      </v-col>

      <v-col cols="12" sm="6" lg="3" class="py-2">
        <v-card class="stat-card glass-card stat-card-4">
          <div class="d-flex justify-content-between align-items-center">
            <div>
              <span class="stat-label">Upload Stats</span>
              <h3 class="stat-value">
                {{
                  EntryMode == "bulk" ? ReviewRows.length : SingleResult ? 1 : 0
                }}
              </h3>
              <div
                class="stat-sublabel mt-1 font-weight-bold d-flex align-center"
              >
                <span v-if="ValidCount > 0" class="success--text mr-2">
                  <v-icon x-small color="#56BDA3">mdi-check-circle</v-icon>
                  {{ ValidCount }} Valid
                </span>
                <span v-if="InvalidCount > 0" class="error--text">
                  <v-icon x-small color="#EF4444">mdi-alert-circle</v-icon>
                  {{ InvalidCount }} Invalid
                </span>
                <span
                  v-if="ValidCount == 0 && InvalidCount == 0"
                  class="text-muted"
                >
                  Ready
                </span>
              </div>
            </div>
            <v-avatar color="rgba(86, 189, 163, 0.15)" size="48">
              <v-icon color="#56BDA3">mdi-cloud-upload</v-icon>
            </v-avatar>
          </div>
        </v-card>
      </v-col>
    </v-row>

    <!-- Enterprise 3-Step Process Navigation Bar -->
    <v-card class="stepper-nav-card glass-card mb-6 animate-form-card" flat>
      <div class="pa-4 pa-md-5">
        <div
          class="stepper-nav-flex d-flex align-center justify-space-between flex-wrap gap-3"
        >
          <!-- Step 1: Select Event & Plan -->
          <div
            class="stepper-node-item d-flex align-center flex-grow-1"
            :class="{
              'node-active': activeStepIndex === 1,
              'node-completed': activeStepIndex > 1,
            }"
          >
            <div class="node-avatar-circle mr-3">
              <v-icon color="white" small v-if="activeStepIndex > 1"
                >mdi-check</v-icon
              >
              <v-icon color="white" small v-else>mdi-calendar</v-icon>
            </div>
            <div>
              <span class="node-step-tag d-block text-caption font-weight-bold"
                >STEP 1</span
              >
              <h4 class="node-title mb-0 font-weight-bold">Event & Plan</h4>
              <span
                class="node-sub-text text-caption text-muted d-none d-sm-block"
              >
                {{ EventId && PlanId ? "Event Selected" : "Select Event" }}
              </span>
            </div>
          </div>

          <!-- Step 1 to 2 Connector -->
          <div
            class="stepper-nav-connector"
            :class="{ 'connector-active': activeStepIndex >= 2 }"
          ></div>

          <!-- Step 2: Member / Excel File Info -->
          <div
            class="stepper-node-item d-flex align-center flex-grow-1"
            :class="{
              'node-active': activeStepIndex === 2,
              'node-completed': activeStepIndex > 2,
              'node-disabled': activeStepIndex < 2,
            }"
          >
            <div class="node-avatar-circle mr-3">
              <v-icon color="white" small v-if="activeStepIndex > 2"
                >mdi-check</v-icon
              >
              <v-icon color="white" small v-else>
                {{
                  EntryMode === "single"
                    ? "mdi-account-search"
                    : "mdi-file-excel"
                }}
              </v-icon>
            </div>
            <div>
              <span class="node-step-tag d-block text-caption font-weight-bold"
                >STEP 2</span
              >
              <h4 class="node-title mb-0 font-weight-bold">
                {{ EntryMode === "single" ? "Member Search" : "Excel Import" }}
              </h4>
              <span
                class="node-sub-text text-caption text-muted d-none d-sm-block"
              >
                {{
                  EntryMode === "single"
                    ? MemberInfo.MemberName
                      ? MemberInfo.MemberName
                      : "Find Member"
                    : ReviewRows.length > 0
                    ? `${ReviewRows.length} Rows Ready`
                    : "Upload Excel"
                }}
              </span>
            </div>
          </div>

          <!-- Step 2 to 3 Connector -->
          <div
            class="stepper-nav-connector"
            :class="{ 'connector-active': activeStepIndex >= 3 }"
          ></div>

          <!-- Step 3: Payment & Final Execution -->
          <div
            class="stepper-node-item d-flex align-center flex-grow-1"
            :class="{
              'node-active': activeStepIndex === 3,
              'node-completed':
                SingleResult || (BulkResults && BulkResults.length > 0),
              'node-disabled': activeStepIndex < 3,
            }"
          >
            <div class="node-avatar-circle mr-3">
              <v-icon
                color="white"
                small
                v-if="SingleResult || (BulkResults && BulkResults.length > 0)"
                >mdi-check-decagram</v-icon
              >
              <v-icon color="white" small v-else>mdi-credit-card-check</v-icon>
            </div>
            <div>
              <span class="node-step-tag d-block text-caption font-weight-bold"
                >STEP 3</span
              >
              <h4 class="node-title mb-0 font-weight-bold">
                Payment & Confirm
              </h4>
              <span
                class="node-sub-text text-caption text-muted d-none d-sm-block"
              >
                Submit & Process
              </span>
            </div>
          </div>
        </div>
      </div>
    </v-card>

    <v-container fluid class="pa-0">
      <!-- Event / Plan selection Card -->
      <v-card class="form-card glass-card mb-6 animate-form-card">
        <div class="form-card-header d-flex align-center pa-5">
          <div class="header-avatar-circle mr-3">
            <v-icon color="white">mdi-calendar</v-icon>
          </div>
          <div>
            <h2 class="form-card-title">Select Event & Plan</h2>
            <p class="form-card-subtitle">
              Only Natcon events are listed here &mdash; other events already
              have their own registration pages
            </p>
          </div>
        </div>

        <v-card-text class="pa-6">
          <v-row>
            <v-col cols="12" md="6">
              <label class="field-label">
                <span class="text-danger">*</span> Event
              </label>
              <v-select
                v-model="EventId"
                :items="EventOptions"
                :rules="EventRules"
                :reduce="(option) => option.value"
                @change="onEventChange"
                required
                outlined
                dense
                rounded
                placeholder="Choose an Event..."
                class="premium-select"
              ></v-select>
            </v-col>

            <v-col cols="12" md="6">
              <label class="field-label">
                <span class="text-danger">*</span> Plan
              </label>
              <v-select
                v-model="PlanId"
                :items="PlanOptions"
                :rules="PlanRules"
                :reduce="(option) => option.value"
                :loading="PlanOptionsLoading"
                required
                outlined
                dense
                rounded
                placeholder="Choose a Plan..."
                class="premium-select"
              >
                <template v-slot:item="{ item }">
                  <div
                    class="d-flex align-center justify-space-between w-100 pa-1"
                  >
                    <span>{{ item.text }}</span>
                    <v-chip
                      x-small
                      color="error"
                      outlined
                      class="ml-2 font-weight-bold"
                      v-if="!item.active"
                      >Inactive</v-chip
                    >
                  </div>
                </template>
                <template v-slot:selection="{ item }">
                  <span class="font-weight-medium">{{ item.text }}</span>
                  <v-chip
                    x-small
                    color="error"
                    outlined
                    class="ml-2 font-weight-bold"
                    v-if="!item.active"
                    >Inactive</v-chip
                  >
                </template>
              </v-select>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>

      <template v-if="EventId != '' && PlanId != ''">
        <!-- Entry Mode Toggle Card -->
        <v-card class="form-card glass-card mb-6 animate-form-card">
          <v-card-text class="pa-6 text-center">
            <div class="entry-mode-wrapper d-inline-block pa-1">
              <v-btn-toggle
                v-model="EntryMode"
                mandatory
                active-class="premium-toggle-active"
                class="entry-mode-toggle-segmented"
              >
                <v-btn value="single" class="segmented-btn">
                  <v-icon left small color="white">mdi-account</v-icon>
                  Single Entry
                </v-btn>
                <v-btn value="bulk" class="segmented-btn">
                  <v-icon left small color="white">mdi-file-excel</v-icon>
                  Bulk Entry
                </v-btn>
              </v-btn-toggle>
            </div>
          </v-card-text>
        </v-card>

        <!-- Single Entry Workflow -->
        <template v-if="EntryMode == 'single'">
          <!-- Member Search Card -->
          <v-card class="form-card glass-card mb-6 animate-form-card">
            <div class="form-card-header d-flex align-center pa-5">
              <div class="header-avatar-circle mr-3">
                <v-icon color="white">mdi-account-search</v-icon>
              </div>
              <div>
                <h2 class="form-card-title">Find Member</h2>
                <p class="form-card-subtitle">
                  Search the member by Membership ID
                </p>
              </div>
            </div>

            <v-card-text class="pa-6">
              <v-row align="end">
                <v-col cols="12" sm="8" md="6">
                  <label class="field-label">
                    <span class="text-danger">*</span> Membership ID
                  </label>
                  <v-text-field
                    v-model="MembershipId"
                    placeholder="Enter Membership ID"
                    outlined
                    dense
                    rounded
                    clearable
                    required
                    prepend-inner-icon="mdi-account-card-details"
                    class="premium-select"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" sm="4" md="3">
                  <v-btn
                    rounded
                    large
                    block
                    class="btn-action-primary white--text"
                    @click="searchMember"
                    :loading="MemberSearchLoading"
                  >
                    <v-icon left small>mdi-magnify</v-icon>
                    Search
                  </v-btn>
                </v-col>
              </v-row>

              <!-- Luxury Member Profile Card -->
              <v-card
                class="member-profile-card mt-5 animate-profile-card"
                v-if="MemberInfo.MemberName"
                flat
              >
                <div
                  class="member-profile-inner pa-4 pa-md-5 d-flex align-center justify-space-between flex-wrap gap-3"
                >
                  <div class="d-flex align-center flex-wrap flex-grow-1 pr-2">
                    <v-avatar size="58" class="mr-4 profile-avatar elevation-3">
                      <img
                        :src="MemberInfo.MemberImage"
                        :alt="MemberInfo.MemberName"
                        v-if="MemberInfo.MemberImage"
                      />
                      <span class="white--text text-h5 font-weight-bold" v-else>
                        {{
                          MemberInfo.MemberName
                            ? MemberInfo.MemberName.charAt(0)
                            : "M"
                        }}
                      </span>
                    </v-avatar>
                    <div>
                      <div class="d-flex align-center flex-wrap gap-2 mb-1">
                        <h3
                          class="member-name white--text mb-0 font-weight-bold"
                        >
                          {{ MemberInfo.MemberName }}
                        </h3>
                        <v-chip
                          x-small
                          color="#56BDA3"
                          class="white--text font-weight-bold ml-2 shadow-sm"
                        >
                          <v-icon x-small left color="white"
                            >mdi-shield-check</v-icon
                          >
                          Verified Member
                        </v-chip>
                      </div>

                      <div class="d-flex align-center flex-wrap gap-2 mt-2">
                        <v-chip
                          small
                          class="profile-info-chip"
                          color="rgba(255, 255, 255, 0.25)"
                          text-color="white"
                        >
                          <v-icon x-small left color="white"
                            >mdi-account-card-details</v-icon
                          >
                          <strong>ID:</strong>&nbsp;{{
                            MemberInfo.MembershipId
                          }}
                        </v-chip>

                        <v-chip
                          small
                          class="profile-info-chip"
                          color="rgba(255, 255, 255, 0.25)"
                          text-color="white"
                          v-if="MemberInfo.ZoneName"
                        >
                          <v-icon x-small left color="white"
                            >mdi-map-marker</v-icon
                          >
                          <strong>Zone:</strong>&nbsp;{{ MemberInfo.ZoneName }}
                        </v-chip>

                        <v-chip
                          small
                          class="profile-info-chip"
                          color="rgba(255, 255, 255, 0.25)"
                          text-color="white"
                          v-if="MemberInfo.LomName"
                        >
                          <v-icon x-small left color="white">mdi-domain</v-icon>
                          <strong>LO:</strong>&nbsp;{{ MemberInfo.LomName }}
                        </v-chip>
                      </div>
                    </div>
                  </div>

                  <!-- Right-aligned Account Status Widget to eliminate empty space -->
                  <div
                    class="d-none d-sm-flex align-center text-right ml-auto pl-4 border-left-glass"
                  >
                    <div class="text-right">
                      <span
                        class="d-block text-caption white--text font-weight-bold opacity-8 text-uppercase"
                        style="letter-spacing: 0.5px"
                        >Account Status</span
                      >
                      <v-chip
                        small
                        color="rgba(255, 255, 255, 0.22)"
                        class="white--text font-weight-bold mt-1 shadow-sm"
                      >
                        <v-icon x-small left color="#56BDA3"
                          >mdi-check-circle</v-icon
                        >
                        Active & Eligible
                      </v-chip>
                    </div>
                  </div>
                </div>
              </v-card>
            </v-card-text>
          </v-card>

          <!-- Payment Information Card -->
          <v-card
            class="form-card glass-card mb-6 animate-form-card"
            v-if="MemberInfo.MemberName"
          >
            <div class="form-card-header d-flex align-center pa-5">
              <div class="header-avatar-circle mr-3">
                <v-icon color="white">mdi-cash</v-icon>
              </div>
              <div>
                <h2 class="form-card-title">Payment Information</h2>
                <p class="form-card-subtitle">
                  Details of the offline payment already received
                </p>
              </div>
            </div>

            <v-card-text class="pa-6">
              <v-form ref="form1" v-model="valid1" lazy-validation>
                <v-row>
                  <v-col cols="12" sm="6" md="3">
                    <label class="field-label">Paid Date</label>
                    <v-menu
                      ref="paidDateMenu"
                      v-model="paidDateMenu"
                      :close-on-content-click="false"
                      transition="scale-transition"
                      offset-y
                      min-width="auto"
                    >
                      <template v-slot:activator="{ on, attrs }">
                        <v-text-field
                          v-model="PaidDate"
                          outlined
                          dense
                          rounded
                          readonly
                          prepend-inner-icon="mdi-calendar"
                          v-bind="attrs"
                          v-on="on"
                          :rules="[(v) => !!v || 'Paid Date is required']"
                          class="premium-select"
                        ></v-text-field>
                      </template>
                      <v-date-picker
                        v-model="PaidDate"
                        @input="paidDateMenu = false"
                        color="#3A67B1"
                      ></v-date-picker>
                    </v-menu>
                  </v-col>

                  <v-col cols="12" sm="6" md="3">
                    <label class="field-label">Transaction Type</label>
                    <v-select
                      v-model="TransactionType"
                      :items="TransactionTypeOptions"
                      outlined
                      dense
                      rounded
                      prepend-inner-icon="mdi-bank"
                      :rules="[(v) => !!v || 'Transaction Type is required']"
                      class="premium-select"
                    ></v-select>
                  </v-col>

                  <v-col cols="12" sm="6" md="3">
                    <label class="field-label">Payment / Transaction ID</label>
                    <v-text-field
                      v-model="PaymentId"
                      outlined
                      dense
                      rounded
                      placeholder="Transaction ID"
                      prepend-inner-icon="mdi-receipt"
                      :rules="[
                        (v) => !!v || 'Payment/Transaction ID is required',
                      ]"
                      class="premium-select"
                    ></v-text-field>
                  </v-col>

                  <v-col cols="12" sm="6" md="3">
                    <label class="field-label">Amount</label>
                    <v-text-field
                      v-model="Amount"
                      outlined
                      dense
                      rounded
                      type="number"
                      placeholder="0.00"
                      prepend-inner-icon="mdi-currency-inr"
                      :rules="[(v) => !!v || 'Amount is required']"
                      class="premium-select"
                    ></v-text-field>
                  </v-col>
                </v-row>

                <v-row class="mt-4">
                  <v-col cols="12" class="text-right">
                    <v-btn
                      rounded
                      large
                      class="btn-action-primary white--text"
                      @click="confirmSingleSubmit"
                      :loading="SubmitFlag"
                    >
                      <v-icon left small>mdi-check-circle</v-icon>
                      Submit
                    </v-btn>
                  </v-col>
                </v-row>
              </v-form>
            </v-card-text>
          </v-card>

          <!-- Single Result -->
          <event-manual-entry-results
            v-if="SingleResult"
            :Results="[SingleResult]"
            Title="Entry Result"
          ></event-manual-entry-results>
        </template>

        <!-- Bulk Entry Workflow -->
        <template v-if="EntryMode == 'bulk'">
          <v-card class="form-card glass-card mb-6 animate-form-card">
            <div class="form-card-header d-flex align-center pa-5">
              <div class="header-avatar-circle mr-3">
                <v-icon color="white">mdi-file-excel</v-icon>
              </div>
              <div>
                <h2 class="form-card-title">Bulk Upload</h2>
                <p class="form-card-subtitle">
                  Download the template, fill Membership ID / Payment ID / Paid
                  Date per row (Plan amount with tax is fetched automatically)
                </p>
              </div>
            </div>

            <v-card-text class="pa-6">
              <v-row align="center" class="mb-4">
                <v-col cols="12" sm="6">
                  <a
                    :href="SampleFilePath"
                    :download="SampleFilePath"
                    class="download-btn-pill"
                  >
                    <v-icon left small color="white">mdi-download</v-icon>
                    Download Template
                  </a>
                </v-col>
              </v-row>

              <v-row align="end">
                <v-col cols="12" sm="8" md="6">
                  <label class="field-label">Excel File</label>
                  <v-file-input
                    v-model="UploadedFile1"
                    accept=".xls,.xlsx"
                    placeholder="Choose Excel file"
                    prepend-icon=""
                    prepend-inner-icon="mdi-file-excel"
                    truncate-length="25"
                    :clearable="true"
                    :rules="[(v) => !!v || 'File is required']"
                    outlined
                    dense
                    rounded
                    class="premium-select"
                  ></v-file-input>
                </v-col>
                <v-col cols="12" sm="4" md="3">
                  <v-btn
                    rounded
                    large
                    block
                    class="btn-action-primary white--text"
                    @click="reviewBulkUpload"
                    :loading="ReviewFlag"
                  >
                    <v-icon left small>mdi-clipboard-text</v-icon>
                    Review
                  </v-btn>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>

          <!-- Final Review Cards -->
          <v-card
            class="form-card glass-card mb-6 animate-form-card"
            v-if="ReviewRows.length > 0"
          >
            <div
              class="form-card-header d-flex align-center justify-space-between flex-wrap gap-2 pa-5"
            >
              <div class="d-flex align-center">
                <div class="header-avatar-circle mr-3">
                  <v-icon color="white">mdi-clipboard-text</v-icon>
                </div>
                <div>
                  <h2 class="form-card-title">Final Review</h2>
                  <p class="form-card-subtitle">
                    {{ ValidCount }} ready to upload, {{ InvalidCount }} have
                    errors
                  </p>
                </div>
              </div>

              <div class="d-flex align-center">
                <v-chip
                  color="rgba(255,255,255,0.2)"
                  class="white--text font-weight-bold mr-2"
                >
                  <v-icon x-small left color="#56BDA3">mdi-check-circle</v-icon>
                  {{ ValidCount }} Valid
                </v-chip>
                <v-chip
                  color="rgba(255,255,255,0.2)"
                  class="white--text font-weight-bold"
                  v-if="InvalidCount > 0"
                >
                  <v-icon x-small left color="#EF4444">mdi-alert-circle</v-icon>
                  {{ InvalidCount }} Invalid
                </v-chip>
              </div>
            </div>

            <v-card-text class="pa-6">
              <v-row>
                <v-col
                  cols="12"
                  md="6"
                  v-for="(row, index) in ReviewRows"
                  :key="index"
                >
                  <v-card
                    class="review-row-card-premium animate-review-row"
                    :style="`animation-delay: ${index * 0.05}s`"
                    :class="
                      row.Valid
                        ? 'review-row-valid-theme'
                        : 'review-row-invalid-theme'
                    "
                    flat
                  >
                    <div class="d-flex align-start pa-4">
                      <v-avatar
                        size="40"
                        :color="
                          row.Valid
                            ? 'rgba(86, 189, 163, 0.15)'
                            : 'rgba(239, 68, 68, 0.15)'
                        "
                        class="mr-3 flex-shrink-0"
                      >
                        <v-icon
                          :color="row.Valid ? '#56BDA3' : '#EF4444'"
                          small
                        >
                          {{
                            row.Valid ? "mdi-check-circle" : "mdi-close-circle"
                          }}
                        </v-icon>
                      </v-avatar>
                      <div class="flex-grow-1 pr-2">
                        <div
                          class="d-flex align-center justify-space-between mb-1"
                        >
                          <span class="review-row-name">
                            {{ row.MemberName || row.MembershipId }}
                          </span>
                          <v-chip
                            x-small
                            class="font-weight-bold"
                            :color="
                              row.Valid
                                ? 'rgba(86, 189, 163, 0.15)'
                                : 'rgba(239, 68, 68, 0.15)'
                            "
                            :text-color="row.Valid ? '#2E7D32' : '#C62828'"
                          >
                            {{ row.Valid ? "Valid" : "Invalid" }}
                          </v-chip>
                        </div>
                        <div class="review-row-sub text-caption">
                          <span class="font-weight-medium"
                            >Membership ID: {{ row.MembershipId }}</span
                          >
                          &middot;
                          <span>Payment ID: {{ row.PaymentId }}</span> &middot;
                          <span
                            class="font-weight-bold text--darken-2"
                            style="color: #2e7d32"
                            >₹{{ row.Amount }}</span
                          >
                        </div>
                        <div
                          class="review-row-error mt-2 text-caption"
                          v-if="!row.Valid"
                        >
                          <v-icon x-small color="#EF4444" class="mr-1"
                            >mdi-alert-circle</v-icon
                          >
                          {{ row.ErrorMessage }}
                        </div>
                      </div>
                    </div>
                  </v-card>
                </v-col>
              </v-row>

              <v-row class="mt-4">
                <v-col cols="12" class="text-right">
                  <v-btn
                    rounded
                    large
                    class="btn-action-primary white--text"
                    @click="confirmBulkUpload"
                    :loading="ConfirmFlag"
                    :disabled="ValidCount == 0"
                  >
                    <v-icon left small>mdi-cloud-upload</v-icon>
                    Confirm & Upload
                  </v-btn>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>

          <!-- Bulk Results -->
          <event-manual-entry-results
            v-if="BulkResults.length > 0"
            :Results="BulkResults"
            Title="Upload Results"
          ></event-manual-entry-results>
        </template>
      </template>
    </v-container>
  </div>
</template>

<script>
import common from "@/view/Common.vue";
import companyConfig from "@/company_config.json";
import Swal from "sweetalert2";
import EventManualEntryResults from "@/view/pages/erp/events/jci-event-for-members/EventManualEntryResults.vue";

export default {
  mixins: [common],
  components: { EventManualEntryResults },
  data() {
    return {
      valid1: true,
      LoadingFlag: false,
      ProgressLoadingFlag: false,
      SubmitFlag: false,
      ReviewFlag: false,
      ConfirmFlag: false,
      MemberSearchLoading: false,

      EntryMode: "single",

      EventRules: [(v) => !!v || "Event is required"],
      EventId: "",
      EventTypeId: "",
      EventOptions: [],

      PlanRules: [(v) => !!v || "Plan is required"],
      PlanId: "",
      PlanOptions: [],
      PlanOptionsLoading: false,

      MembershipId: "",
      MemberInfo: {},

      paidDateMenu: false,
      PaidDate: new Date().toISOString().substr(0, 10),
      TransactionType: "",
      PaymentId: "",
      Amount: "",
      TransactionTypeOptions: [
        { text: "Cash", value: "Cash" },
        { text: "Cheque", value: "Cheque" },
        { text: "DD", value: "DD" },
        { text: "Bank Transfer", value: "Bank Transfer" },
        { text: "UPI", value: "UPI" },
      ],

      SingleResult: null,

      UploadedFile1: [],
      ReviewRows: [],
      ValidCount: 0,
      InvalidCount: 0,
      BulkResults: [],

      PageInfo: {},
      PageTitle: "Event Manual Entry",
      PageDescription: "Record offline event payments one at a time or in bulk",
    };
  },
  computed: {
    SampleFilePath() {
      return (
        companyConfig.apiURL +
        "assets/sample/event_manual_entry_bulk_template.xlsx"
      );
    },
    selectedEventText() {
      if (!this.EventId || !this.EventOptions) return "None Selected";
      var found = this.EventOptions.find((e) => e.value == this.EventId);
      return found ? found.text || found.label || "Selected" : "Selected";
    },
    selectedPlanText() {
      if (!this.PlanId || !this.PlanOptions) return "None Selected";
      var found = this.PlanOptions.find((p) => p.value == this.PlanId);
      return found ? found.text || "Selected" : "Selected";
    },
    activeStepIndex() {
      if (!this.EventId || !this.PlanId) return 1;
      if (this.EntryMode === "single") {
        if (!this.MemberInfo || !this.MemberInfo.MemberName) return 2;
        return 3;
      } else {
        if (this.ReviewRows.length === 0) return 2;
        return 3;
      }
    },
  },
  watch: {
    PageInfo: function () {
      this.LoadingFlag = false;
      var tr = this.PageInfo;
      var n1 = Object.keys(tr).length;
      if (n1 > 0) {
        if (!tr.PagePermissionFlag) {
          this.$router.push("/access-denied");
        }
        if (tr.PageTitle && tr.PageTitle.trim() !== "") {
          this.PageTitle = tr.PageTitle;
        }
        if (tr.PageDescription && tr.PageDescription.trim() !== "") {
          this.PageDescription = tr.PageDescription;
        }
        this.pageData();
      }
    },
    EventId: function () {
      this.PlanId = "";
      this.PlanOptions = [];
      this.Amount = "";
    },
    PlanId: function (newPlanId) {
      if (newPlanId && this.PlanOptions.length > 0) {
        var selectedPlan = this.PlanOptions.find(function (p) {
          return p.value == newPlanId;
        });
        if (selectedPlan && selectedPlan.amount) {
          this.Amount = selectedPlan.amount;
        }
      }
    },
  },
  methods: {
    refreshPageData() {
      this.LoadingFlag = true;
      var url1 = "api/menu-details/show";
      var condition1 = {
        UserInterface: 1,
        Module: "event_manual_entry",
        Action: "create",
      };
      this.getPageDetails("PageInfo", {}, url1, condition1);
    },
    pageData() {
      this.getEventOptions();
    },
    getEventOptions() {
      var selectbox1_source = "EventId";
      var selectbox1_destination = "EventOptions";
      var selectbox1_url = "api/jci-program/options";
      var selectbox1_conditions_array = {
        UserInterface: 5,
      };
      this.getSelectBoxOptions(
        selectbox1_source,
        selectbox1_destination,
        selectbox1_conditions_array,
        selectbox1_url
      );
    },
    onEventChange() {
      var EventId = this.EventId;
      var selected = this.EventOptions.find(function (option) {
        return option.value == EventId;
      });
      this.EventTypeId = selected ? selected.ProgramTypeId : "";
      this.getPlanOptions();
    },
    getPlanOptions() {
      var EventId = this.EventId;
      var EventTypeId = this.EventTypeId;
      if (EventId == "" || EventTypeId == "") {
        return;
      }
      this.PlanOptionsLoading = true;
      this.PlanOptions = [];

      var server_url = companyConfig.apiURL;
      var token = this.$session.get("token");
      token = token == (null || undefined) ? 0 : token;
      var add_url = server_url + "api/jci-program/plan/lists";
      var upload = {
        UserInterface: 2,
        Program: EventId,
        ProgramType: EventTypeId,
      };

      const thisIns = this;

      this.$http({
        url: add_url,
        method: "POST",
        data: upload,
        headers: {
          "Content-Type": "application/json",
          Authorization: "Bearer " + token,
        },
      })
        .then(function (response) {
          thisIns.PlanOptionsLoading = false;
          if (response.data.flag == 1 || response.data.flag == true) {
            var records = response.data.records.TableData || [];
            thisIns.PlanOptions = records.map(function (item) {
              return {
                value: item.EventPlanId,
                text: item.PlanName,
                amount: item.TotalAmount || item.SubTotal || 0,
                active: item.PlanVisibleFlag,
              };
            });
          } else {
            thisIns.toast("error", response.data.output);
          }
        })
        .catch(function () {
          thisIns.PlanOptionsLoading = false;
        });
    },
    searchMember() {
      var MembershipId = this.MembershipId;
      if (MembershipId == "" || MembershipId == null) {
        this.sweetAlert("error", "Kindly enter a Membership ID. ", false);
        return;
      }
      this.MemberSearchLoading = true;
      this.MemberInfo = {};
      this.SingleResult = null;

      var server_url = companyConfig.apiURL;
      var token = this.$session.get("token");
      token = token == (null || undefined) ? 0 : token;
      var add_url = server_url + "api/members/search";
      var upload = {
        UserInterface: 2,
        MembershipId: MembershipId,
      };

      const thisIns = this;

      this.$http({
        url: add_url,
        method: "POST",
        data: upload,
        headers: {
          "Content-Type": "application/json",
          Authorization: "Bearer " + token,
        },
      })
        .then(function (response) {
          thisIns.MemberSearchLoading = false;
          if (response.data.flag == 1 || response.data.flag == true) {
            var tr = response.data.records || {};
            var name =
              tr.Fullname ||
              tr.MemberName ||
              [
                tr.member_details_first_name,
                tr.member_details_middle_name,
                tr.member_details_last_name,
              ]
                .filter(Boolean)
                .join(" ");
            var lom =
              tr.LomName ||
              (tr.lom ? tr.lom.lom_name : tr.member_details_lom_name || "");
            var zone =
              tr.ZoneName ||
              (tr.zone ? tr.zone.zone_name : tr.member_details_zone_name || "");
            var photo =
              tr.MemberImage ||
              tr.PhotoPath ||
              tr.member_details_photo ||
              tr.profile_picture ||
              tr.image ||
              tr.photo ||
              "";
            var memId =
              tr.MembershipId ||
              tr.member_details_membership_id ||
              MembershipId;

            thisIns.MemberInfo = {
              MemberName: name,
              MembershipId: memId,
              LomName: lom,
              ZoneName: zone,
              MemberImage: photo,
            };
          } else {
            thisIns.toast("error", response.data.output);
          }
        })
        .catch(function () {
          thisIns.MemberSearchLoading = false;
        });
    },
    confirmSingleSubmit() {
      var validate1 = this.$refs.form1.validate();
      if (!validate1 || !this.MemberInfo.MemberName) {
        this.sweetAlert(
          "error",
          "Kindly fill all required fields and search a valid member. ",
          false
        );
        return;
      }
      var htmlTxt = "";
      htmlTxt +=
        "<br/>Member <b>" +
        this.MemberInfo.MemberName +
        " (" +
        this.MemberInfo.MembershipId +
        ")</b>";
      htmlTxt += "<br/>Payment / Transaction ID <b>" + this.PaymentId + "</b>";
      htmlTxt += "<br/>Amount <b>&#8377; " + this.Amount + "</b>";
      htmlTxt += "<br/><br/>Do you want to continue?";
      Swal.fire({
        title: "Please verify all the information before proceeding",
        html: htmlTxt,
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Continue",
        cancelButtonText: "Cancel",
      }).then((result) => {
        if (result.isConfirmed) {
          this.submitSingle();
        }
      });
    },
    submitSingle() {
      this.SubmitFlag = true;
      this.SingleResult = null;

      var server_url = companyConfig.apiURL;
      var token = this.$session.get("token");
      token = token == (null || undefined) ? 0 : token;
      var add_url = server_url + "api/jci-program/manual-entry/single";
      var upload = {
        Event: this.EventId,
        Plan: this.PlanId,
        MembershipId: this.MembershipId,
        PaidDate: this.PaidDate,
        TransactionType: this.TransactionType,
        PaymentId: this.PaymentId,
        Amount: this.Amount,
      };

      const thisIns = this;

      this.$http({
        url: add_url,
        method: "POST",
        data: upload,
        headers: {
          "Content-Type": "application/json",
          Authorization: "Bearer " + token,
        },
      })
        .then(function (response) {
          thisIns.SubmitFlag = false;
          if (response.data.flag == 1 || response.data.flag == true) {
            thisIns.sweetAlert("success", response.data.output, true);
            thisIns.SingleResult = response.data.records;
            thisIns.PaymentId = "";
            thisIns.Amount = "";
          } else {
            thisIns.sweetAlert("error", response.data.output, false);
          }
        })
        .catch(function () {
          thisIns.SubmitFlag = false;
        });
    },
    reviewBulkUpload() {
      if (
        this.UploadedFile1.length == 0 &&
        !(this.UploadedFile1 instanceof File)
      ) {
        this.toast("error", "Kindly choose an Excel file. ");
        return;
      }
      this.ReviewFlag = true;
      this.ReviewRows = [];
      this.BulkResults = [];

      var server_url = companyConfig.apiURL;
      var token = this.$session.get("token");
      token = token == (null || undefined) ? 0 : token;
      var add_url = server_url + "api/jci-program/manual-entry/bulk-review";

      let upload = new FormData();
      upload.append("Event", this.EventId);
      upload.append("Plan", this.PlanId);
      upload.append("UploadedFile", this.UploadedFile1);

      const thisIns = this;

      this.$http({
        url: add_url,
        method: "POST",
        data: upload,
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: "Bearer " + token,
        },
      })
        .then(function (response) {
          thisIns.ReviewFlag = false;
          var records = response.data.records || {};
          thisIns.ReviewRows = records.TableData || [];
          thisIns.ValidCount = records.ValidCount || 0;
          thisIns.InvalidCount = records.InvalidCount || 0;
          thisIns.toast(
            response.data.flag == 1 || response.data.flag == true
              ? "success"
              : "error",
            response.data.output
          );
        })
        .catch(function () {
          thisIns.ReviewFlag = false;
        });
    },
    confirmBulkUpload() {
      var validRows = this.ReviewRows.filter(function (row) {
        return row.Valid;
      });
      if (validRows.length == 0) {
        this.toast("error", "There are no valid records to upload. ");
        return;
      }
      this.ConfirmFlag = true;

      var server_url = companyConfig.apiURL;
      var token = this.$session.get("token");
      token = token == (null || undefined) ? 0 : token;
      var add_url = server_url + "api/jci-program/manual-entry/bulk-confirm";
      var upload = {
        Event: this.EventId,
        Plan: this.PlanId,
        Rows: validRows.map(function (row) {
          return {
            MembershipId: row.MembershipId,
            PaymentId: row.PaymentId,
            Amount: row.Amount,
            PaidDate: row.PaidDate,
          };
        }),
      };

      const thisIns = this;

      this.$http({
        url: add_url,
        method: "POST",
        data: upload,
        headers: {
          "Content-Type": "application/json",
          Authorization: "Bearer " + token,
        },
      })
        .then(function (response) {
          thisIns.ConfirmFlag = false;
          var records = response.data.records || {};
          thisIns.BulkResults = records.Results || [];
          thisIns.ReviewRows = [];
          thisIns.UploadedFile1 = [];
          thisIns.toast(
            response.data.flag == 1 || response.data.flag == true
              ? "success"
              : "error",
            response.data.output
          );
        })
        .catch(function () {
          thisIns.ConfirmFlag = false;
        });
    },
  },
  beforeMount() {
    this.refreshPageData();
  },
};
</script>

<style scoped>
@import url("https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;500;600;700;800;900&family=Plus+Jakarta+Sans:wght@300;400;500;600;700;800&display=swap");

.premium-dashboard {
  font-family: "Outfit", "Plus Jakarta Sans", sans-serif;
  background-color: #f6f8fc;
  min-height: 100vh;
  position: relative;
  overflow: hidden;
  padding: 16px;
}

/* Floating light ambient background orbs */
.glow-orb {
  position: absolute;
  border-radius: 50%;
  pointer-events: none;
  opacity: 0.15;
  z-index: 0;
}

.orb-1 {
  width: 450px;
  height: 450px;
  background: radial-gradient(circle, #0097d7 0%, rgba(58, 103, 177, 0) 70%);
  top: -100px;
  right: -50px;
}

.orb-2 {
  width: 500px;
  height: 500px;
  background: radial-gradient(
    circle,
    rgba(58, 103, 177, 0.25) 0%,
    rgba(0, 151, 215, 0) 70%
  );
  bottom: -150px;
  left: -100px;
}

/* Hero Dashboard Header */
.hero-card {
  background: linear-gradient(
    135deg,
    #3a67b1 0%,
    #1b4c9e 45%,
    #0097d7 100%
  ) !important;
  border-radius: 20px !important;
  color: white;
  box-shadow: 0 16px 40px rgba(58, 103, 177, 0.28) !important;
  overflow: hidden;
  position: relative;
  z-index: 1;
}

.animate-hero-card {
  animation: heroSlideDown 0.8s cubic-bezier(0.16, 1, 0.3, 1) forwards;
}

.hero-glow-effect {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(
    135deg,
    rgba(255, 255, 255, 0.12) 0%,
    rgba(255, 255, 255, 0) 100%
  );
  z-index: 0;
  pointer-events: none;
}

.hero-glow-effect::after {
  content: "";
  position: absolute;
  top: 0;
  left: -50%;
  width: 200%;
  height: 100%;
  background: linear-gradient(
    to right,
    rgba(255, 255, 255, 0) 0%,
    rgba(255, 255, 255, 0.15) 30%,
    rgba(255, 255, 255, 0) 60%
  );
  transform: skewX(-25deg);
  animation: shineSweep 6s infinite ease-in-out;
}

.relative-content {
  position: relative;
  z-index: 2;
}

.hero-title {
  font-size: 28px;
  font-weight: 800;
  letter-spacing: -0.8px;
  line-height: 1.2;
  text-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
}

.hero-subtitle {
  font-size: 14px;
  opacity: 0.95;
  margin-top: 6px;
  max-width: 650px;
  font-weight: 400;
}

.header-icon-box {
  background: rgba(255, 255, 255, 0.22);
  border-radius: 16px;
  padding: 12px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.header-action-btn {
  font-weight: 700 !important;
  letter-spacing: 0.3px !important;
  background: rgba(255, 255, 255, 0.18) !important;
  border: 1.5px solid rgba(255, 255, 255, 0.35) !important;
  color: white !important;
  transition: all 0.3s ease !important;
  text-transform: none !important;
}

.header-action-btn:hover {
  background: rgba(255, 255, 255, 0.28) !important;
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.15) !important;
}

/* Glassmorphism Cards with crisp rendering */
.glass-card {
  background: #ffffff !important;
  border: 1px solid rgba(58, 103, 177, 0.12) !important;
  border-radius: 20px !important;
  box-shadow: 0 10px 30px rgba(33, 33, 33, 0.05) !important;
  overflow: hidden;
  position: relative;
  z-index: 1;
}

/* Stat Cards */
.stat-card {
  padding: 22px;
  position: relative;
  overflow: hidden;
  transition: box-shadow 0.3s ease, border-color 0.3s ease !important;
}

.stat-card:hover {
  box-shadow: 0 18px 40px rgba(58, 103, 177, 0.18) !important;
  border-color: rgba(58, 103, 177, 0.4) !important;
}

.stat-card-1 {
  animation: cardPopIn 0.6s cubic-bezier(0.34, 1.56, 0.64, 1) 0.1s forwards;
}
.stat-card-2 {
  animation: cardPopIn 0.6s cubic-bezier(0.34, 1.56, 0.64, 1) 0.2s forwards;
}
.stat-card-3 {
  animation: cardPopIn 0.6s cubic-bezier(0.34, 1.56, 0.64, 1) 0.3s forwards;
}
.stat-card-4 {
  animation: cardPopIn 0.6s cubic-bezier(0.34, 1.56, 0.64, 1) 0.4s forwards;
}

.stat-card-glow {
  position: absolute;
  width: 100px;
  height: 100px;
  border-radius: 50%;
  top: -40px;
  right: -40px;
  opacity: 0.08;
}

.orb-event {
  background-color: #3a67b1;
}
.orb-plan {
  background-color: #0097d7;
}
.orb-mode {
  background-color: #edbe38;
}
.orb-status {
  background-color: #56bda3;
}

.stat-label {
  font-size: 12px;
  font-weight: 800;
  text-transform: uppercase;
  color: #1e293b !important;
  letter-spacing: 0.8px;
}

.stat-value {
  font-size: 20px;
  font-weight: 800;
  color: #0f172a !important;
  margin-top: 4px;
  letter-spacing: -0.5px;
}

.stat-sublabel {
  font-size: 13px;
  color: #334155 !important;
  font-weight: 600 !important;
}

/* Form Card Styling */
.form-card {
  transition: box-shadow 0.3s ease, border-color 0.3s ease !important;
}

.form-card:hover {
  box-shadow: 0 18px 45px rgba(58, 103, 177, 0.16) !important;
}

.animate-form-card {
  animation: cardSlideUp 0.7s cubic-bezier(0.16, 1, 0.3, 1) forwards;
}

.form-card-header {
  background: linear-gradient(90deg, #3a67b1 0%, #0097d7 100%);
  padding: 18px 24px;
  color: white;
}

.header-avatar-circle {
  width: 44px;
  height: 44px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.22);
  border: 1.5px solid rgba(255, 255, 255, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.form-card-title {
  font-size: 18px;
  font-weight: 800;
  color: white;
  margin-bottom: 2px;
}

.form-card-subtitle {
  font-size: 13px;
  color: #ffffff !important;
  font-weight: 600 !important;
  margin-bottom: 0;
}

.field-label {
  font-size: 13px;
  font-weight: 800;
  color: #0f172a !important;
  margin-bottom: 6px;
  display: block;
}

/* Inputs & Autocompletes styling */
.premium-select >>> .v-input__control > .v-input__slot {
  border-radius: 12px !important;
  border: 1.5px solid rgba(58, 103, 177, 0.25) !important;
  background-color: white !important;
  transition: all 0.3s ease !important;
}

.premium-select >>> .v-input__control > .v-input__slot input {
  color: #0f172a !important;
  font-weight: 700 !important;
}

.premium-select >>> .v-select__selection {
  color: #0f172a !important;
  font-weight: 700 !important;
}

.premium-select >>> .v-input__control > .v-input__slot:hover {
  border-color: rgba(58, 103, 177, 0.4) !important;
}

.premium-select.v-input--is-focused >>> .v-input__control > .v-input__slot {
  border-color: #3a67b1 !important;
  box-shadow: 0 0 0 3.5px rgba(58, 103, 177, 0.15) !important;
}

/* Segmented Control Pill Toggle */
.entry-mode-wrapper {
  background: rgba(58, 103, 177, 0.08);
  border-radius: 16px;
  padding: 4px;
}

.entry-mode-toggle-segmented {
  background: transparent !important;
  border: none !important;
}

.entry-mode-toggle-segmented .v-btn {
  border-radius: 12px !important;
  border: none !important;
  font-weight: 700 !important;
  text-transform: none !important;
  letter-spacing: 0.3px !important;
  padding: 0 28px !important;
  height: 44px !important;
  color: #546e7a !important;
  transition: all 0.3s ease !important;
}

.entry-mode-toggle-segmented .v-btn.premium-toggle-active {
  background: linear-gradient(135deg, #3a67b1 0%, #0097d7 100%) !important;
  color: white !important;
  box-shadow: 0 6px 18px rgba(58, 103, 177, 0.35) !important;
}

.entry-mode-toggle-segmented .v-btn .v-icon {
  color: white !important;
}

/* Action Buttons */
.btn-action-primary {
  background: linear-gradient(135deg, #3a67b1 0%, #0097d7 100%) !important;
  font-weight: 700 !important;
  text-transform: none !important;
  letter-spacing: 0.3px !important;
  box-shadow: 0 6px 20px rgba(58, 103, 177, 0.3) !important;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
}

.btn-action-primary:hover {
  transform: translateY(-2px);
  box-shadow: 0 10px 25px rgba(58, 103, 177, 0.45) !important;
}

/* Searched Member Profile Card */
.member-profile-card {
  background: linear-gradient(135deg, #3a67b1 0%, #0097d7 100%) !important;
  border-radius: 16px !important;
  overflow: hidden;
  box-shadow: 0 10px 28px rgba(58, 103, 177, 0.25) !important;
  transition: box-shadow 0.3s ease !important;
}

.member-profile-card:hover {
  box-shadow: 0 20px 45px rgba(58, 103, 177, 0.35) !important;
}

.animate-profile-card {
  animation: profileCardPop 0.6s cubic-bezier(0.34, 1.56, 0.64, 1) forwards;
}

.profile-avatar {
  background: rgba(255, 255, 255, 0.2);
  border: 2px solid rgba(255, 255, 255, 0.4);
}

.member-name {
  font-size: 20px;
  letter-spacing: -0.3px;
}

.profile-info-chip {
  background: rgba(255, 255, 255, 0.22) !important;
  border: 1px solid rgba(255, 255, 255, 0.4) !important;
  font-size: 13px !important;
  font-weight: 600 !important;
  letter-spacing: 0.2px;
}

.border-left-glass {
  border-left: 1.5px solid rgba(255, 255, 255, 0.25);
}

/* Bulk Upload download pill */
.download-btn-pill {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 22px;
  background: linear-gradient(135deg, #3a67b1 0%, #0097d7 100%);
  color: white !important;
  text-decoration: none;
  border-radius: 12px;
  font-weight: 700;
  font-size: 13px;
  box-shadow: 0 6px 18px rgba(0, 151, 215, 0.3);
  transition: all 0.3s ease;
}

.download-btn-pill:hover {
  transform: translateY(-2px);
  box-shadow: 0 10px 22px rgba(0, 151, 215, 0.45);
}

/* Final Review Validation Cards */
.review-row-card-premium {
  border-radius: 14px !important;
  transition: box-shadow 0.3s ease, border-color 0.3s ease !important;
}

.animate-review-row {
  animation: cardPopIn 0.5s cubic-bezier(0.34, 1.56, 0.64, 1) forwards;
}

.review-row-valid-theme {
  background: rgba(86, 189, 163, 0.05) !important;
  border: 1.5px solid rgba(86, 189, 163, 0.35) !important;
}

.review-row-valid-theme:hover {
  box-shadow: 0 12px 30px rgba(86, 189, 163, 0.2) !important;
}

.review-row-invalid-theme {
  background: rgba(239, 68, 68, 0.05) !important;
  border: 1.5px solid rgba(239, 68, 68, 0.35) !important;
}

.review-row-invalid-theme:hover {
  box-shadow: 0 12px 30px rgba(239, 68, 68, 0.2) !important;
}

.review-row-name {
  font-size: 14px;
  font-weight: 700;
  color: #263238;
}

.review-row-sub {
  color: #607d8b;
}

.review-row-error {
  color: #ef4444;
  font-weight: 600;
}

/* Keyframes Definitions */
@keyframes heroSlideDown {
  0% {
    opacity: 0;
    transform: translateY(-30px) scale(0.97);
  }
  100% {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

@keyframes cardSlideUp {
  0% {
    opacity: 0;
    transform: translateY(30px);
  }
  100% {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes cardPopIn {
  0% {
    opacity: 0;
    transform: translateY(20px) scale(0.92);
  }
  100% {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

@keyframes profileCardPop {
  0% {
    opacity: 0;
    transform: scale(0.88) translateY(20px);
  }
  70% {
    transform: scale(1.02) translateY(-4px);
  }
  100% {
    opacity: 1;
    transform: scale(1) translateY(0);
  }
}

/* Ocean Wave Styling */
.waves-container {
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 40px;
  min-height: 40px;
  max-height: 60px;
  z-index: 1;
}

.waves {
  position: relative;
  width: 100%;
  height: 40px;
  margin-bottom: -7px;
}

.parallax > use {
  animation: move-forever-eme 25s cubic-bezier(0.55, 0.5, 0.45, 0.5) infinite;
}

.parallax > use:nth-child(1) {
  animation-delay: -2s;
  animation-duration: 7s;
}

.parallax > use:nth-child(2) {
  animation-delay: -3s;
  animation-duration: 10s;
}

.parallax > use:nth-child(3) {
  animation-delay: -4s;
  animation-duration: 13s;
}

.parallax > use:nth-child(4) {
  animation-delay: -5s;
  animation-duration: 20s;
}

@keyframes move-forever-eme {
  0% {
    transform: translate3d(-90px, 0, 0);
  }
  100% {
    transform: translate3d(85px, 0, 0);
  }
}

/* Keyframes & Decorative elements */
.deco-icon {
  position: absolute !important;
  z-index: 1;
  pointer-events: none;
}

.icon-cash {
  font-size: 100px !important;
  right: 18%;
  top: -15px;
  transform: rotate(12deg);
  animation: floatDeco 12s infinite ease-in-out;
}

.icon-ticket {
  font-size: 75px !important;
  left: 8%;
  bottom: -10px;
  transform: rotate(-10deg);
  animation: floatDeco 14s infinite ease-in-out alternate;
}

.icon-shield {
  font-size: 85px !important;
  right: 4%;
  bottom: -5px;
  transform: rotate(18deg);
  animation: floatDeco 10s infinite ease-in-out;
}

.icon-rupee {
  font-size: 65px !important;
  left: 45%;
  top: -25px;
  animation: floatDeco 15s infinite ease-in-out alternate;
}

.pulse-animation {
  animation: pulseIcon 3s infinite ease-in-out;
}

@keyframes shineSweep {
  0% {
    left: -120%;
  }
  15%,
  100% {
    left: 120%;
  }
}

@keyframes floatDeco {
  0%,
  100% {
    transform: translateY(0) scale(1);
  }
  50% {
    transform: translateY(-10px) scale(1.03);
  }
}

@keyframes pulseIcon {
  0%,
  100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.08);
  }
}

/* Custom Enterprise Stepper Header Styling */
.stepper-nav-card {
  background: #ffffff !important;
  border: 1px solid rgba(58, 103, 177, 0.14) !important;
  border-radius: 18px !important;
  box-shadow: 0 8px 25px rgba(33, 33, 33, 0.04) !important;
}

.stepper-node-item {
  padding: 10px 16px;
  border-radius: 12px;
  background: rgba(245, 247, 250, 0.8);
  border: 1.5px solid transparent;
  transition: all 0.3s ease;
}

.node-active {
  background: rgba(0, 151, 215, 0.06) !important;
  border-color: rgba(0, 151, 215, 0.3) !important;
  box-shadow: 0 4px 15px rgba(0, 151, 215, 0.12) !important;
}

.node-completed {
  background: rgba(86, 189, 163, 0.06) !important;
  border-color: rgba(86, 189, 163, 0.3) !important;
}

.node-disabled {
  opacity: 0.6;
}

.node-avatar-circle {
  width: 38px;
  height: 38px;
  border-radius: 50%;
  background: #90a4ae;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: all 0.3s ease;
}

.node-active .node-avatar-circle {
  background: linear-gradient(135deg, #3a67b1 0%, #0097d7 100%);
  box-shadow: 0 4px 12px rgba(58, 103, 177, 0.3);
}

.node-completed .node-avatar-circle {
  background: linear-gradient(135deg, #56bda3 0%, #2e7d32 100%);
  box-shadow: 0 4px 12px rgba(86, 189, 163, 0.3);
}

.node-step-tag {
  color: #78909c;
  font-size: 10px !important;
  letter-spacing: 0.6px;
}

.node-active .node-step-tag {
  color: #0097d7;
}
.node-completed .node-step-tag {
  color: #56bda3;
}

.node-title {
  font-size: 14px;
  color: #263238;
}

.stepper-nav-connector {
  flex: 1;
  height: 3px;
  background: #e0e0e0;
  min-width: 24px;
  border-radius: 4px;
  transition: all 0.4s ease;
}

.connector-active {
  background: linear-gradient(90deg, #3a67b1 0%, #56bda3 100%) !important;
}

/* Mobile Responsive Media Queries */
@media (max-width: 600px) {
  .hero-title {
    font-size: 20px !important;
  }
  .hero-subtitle {
    font-size: 12px !important;
  }
  .header-icon-box {
    width: 40px !important;
    height: 40px !important;
  }
  .deco-icon {
    display: none !important;
  }
  .stepper-nav-card {
    padding: 8px !important;
  }
  .stepper-nav-flex {
    gap: 6px !important;
  }
  .stepper-node-item {
    padding: 6px 10px !important;
  }
  .node-avatar-circle {
    width: 30px !important;
    height: 30px !important;
  }
  .node-title {
    font-size: 12px !important;
  }
  .stepper-nav-connector {
    min-width: 10px !important;
  }
  .form-card-title {
    font-size: 16px !important;
  }
  .form-card-subtitle {
    font-size: 12px !important;
  }
  .header-avatar-circle {
    width: 36px !important;
    height: 36px !important;
  }
  .stat-value {
    font-size: 16px !important;
  }
  .member-name {
    font-size: 16px !important;
  }
  .btn-action-primary {
    width: 100% !important;
  }
  .segmented-control {
    width: 100% !important;
  }
  .segmented-btn {
    flex: 1 !important;
  }
}
</style>
